/**
 * Shared Ask AI wiring — transport + navigation actions for the assistant.
 */
import * as React from "react";
import {
  createJsonChatTransport,
  safeAppPath,
  type AssistantAction,
  type ResourceRef,
} from "@authe/ai-assistant";
import { useSession } from "@authe/ui-core/session";
import { useNavigate } from "@tanstack/react-router";
import { appConfig, APPLICATION_SLUG, ROUTES } from "@/config";

export const ASK_AI_SUGGESTIONS = [
  "Show high-risk transactions in the last 24 hours",
  "Which entities are bridging funds across chains?",
  "Summarize activity for this tenant this week",
] as const;

export function useAskAiTransport() {
  const { getAccessToken } = useSession();

  return React.useMemo(
    () =>
      createJsonChatTransport({
        url: `${appConfig.apiUrl}/ai/chat`,
        credentials: "omit",
        headers: async (): Promise<Record<string, string>> => {
          const token = await getAccessToken();
          const h: Record<string, string> = { "Source-Platform": APPLICATION_SLUG };
          if (token) h.Authorization = `Bearer ${token}`;
          return h;
        },
      }),
    [getAccessToken],
  );
}

/** Where the router should go, as its `to` and `search`. */
export interface ActionTarget {
  to: string;
  search?: Record<string, string>;
}

/** The app route for a resource the assistant pointed at. */
export function resourceTarget(ref: ResourceRef): ActionTarget {
  switch (ref.kind) {
    case "transaction":
      return { to: ROUTES.transaction(ref.id) };
    case "address":
      // The chain rides as `?chain=`, the page's own convention for a lens.
      return { to: ROUTES.address(ref.id), ...(ref.chain ? { search: { chain: ref.chain } } : {}) };
    case "entity":
      return { to: ROUTES.entity(ref.id) };
    case "group":
      return { to: ROUTES.group(ref.id) };
    case "label":
      return { to: `/labels/${ref.id}` };
    case "tag":
      return { to: `/tags/${ref.id}` };
  }
}

/**
 * The route an action leads to, or `null` when this app cannot apply it.
 *
 * Read off the package's real shape — `kind`, and `href` or `target`. The
 * handler this replaces cast the action to `{ type?: string; to?: unknown }` and
 * tested `a.type === "navigate"`, a field the model does not have. The cast is
 * what hid that from the compiler, and the result was that no navigation the
 * assistant proposed could ever be applied: every Open chip was dead.
 *
 * `href` is split into path and query rather than handed to the router whole,
 * and re-checked with `safeAppPath` even though the transport already gated it,
 * because this is the line that actually moves the page.
 */
export function actionTarget(action: AssistantAction): ActionTarget | null {
  switch (action.kind) {
    case "navigate": {
      const href = safeAppPath(action.href);
      if (href) {
        const url = new URL(href, "http://app.invalid");
        const search = Object.fromEntries(url.searchParams.entries());
        return { to: url.pathname, ...(Object.keys(search).length ? { search } : {}) };
      }
      return action.target ? resourceTarget(action.target) : null;
    }
    case "open_resource":
      return resourceTarget(action.resource);
    default:
      // Graph overlays, table filters, queries and catalog suggestions need a
      // surface to apply them to, and the shell drawer is not one.
      return null;
  }
}

/** Whether {@link useAskAiActionHandler} can carry this action out. */
export function canApplyAskAiAction(action: AssistantAction): boolean {
  return actionTarget(action) !== null;
}

/**
 * Apply an action the reader chose.
 *
 * For `AssistantMessage`'s `onAction` — the Apply button — only. It must NOT be
 * passed to `useAssistant`'s `onAction`, which fires as each action ARRIVES: wired
 * there, a navigate would take the page the instant the model emitted it.
 */
export function useAskAiActionHandler() {
  const navigate = useNavigate();

  return React.useCallback(
    (action: AssistantAction) => {
      const target = actionTarget(action);
      if (!target) return;
      navigate({ to: target.to, ...(target.search ? { search: target.search } : {}) } as never);
    },
    [navigate],
  );
}
